import { nanoid } from "nanoid";
import { DEFAULT_GRID, DEFAULT_IMAGE_SIZE, DEFAULT_LOGO_POSITION, DEFAULT_WELCOME_POSITION, } from "./types/foyer";
import { defaultContentForScheme } from "./schemes/content";
import { defaultSchemeForPremise } from "./schemes/manifests";
export function migrateConfig(raw) {
    const r = raw;
    let cfg = liftLegacyPages(r);
    cfg = liftLegacyContentToComponents(cfg);
    cfg = ensurePageDefaults(cfg);
    cfg = autofixTiles(cfg);
    cfg = migrateFontSizesToPt(cfg);
    cfg = migrateToScheme(cfg);
    cfg = dropAllCustomStations(cfg);
    return cfg;
}
function dropAllCustomStations(cfg) {
    var _a;
    const versioned = cfg;
    if (((_a = versioned.schemaVersion) !== null && _a !== void 0 ? _a : 1) >= 5)
        return cfg;
    if (!cfg.scheme)
        return { ...cfg, schemaVersion: 5 };
    if (cfg.scheme.content.stations.length === 0) {
        return { ...cfg, schemaVersion: 5 };
    }
    return {
        ...cfg,
        scheme: {
            ...cfg.scheme,
            content: { ...cfg.scheme.content, stations: [] },
        },
        schemaVersion: 5,
    };
}
function migrateToScheme(cfg) {
    var _a, _b, _c;
    const versioned = cfg;
    if (((_a = versioned.schemaVersion) !== null && _a !== void 0 ? _a : 1) >= 3)
        return cfg;
    if (cfg.scheme)
        return { ...cfg, schemaVersion: 3 };
    const key = defaultSchemeForPremise(cfg);
    const accent = (_b = cfg.theme) === null || _b === void 0 ? void 0 : _b.accentColor;
    const scheme = {
        key,
        content: {
            ...defaultContentForScheme(key, {
                hotelName: cfg.premiseName,
                city: (_c = cfg.address) === null || _c === void 0 ? void 0 : _c.city,
            }),
            stations: [],
        },
        ...(accent ? { tokens: { palette: { accent } } } : {}),
    };
    return { ...cfg, scheme, schemaVersion: 3 };
}
function migrateFontSizesToPt(cfg) {
    var _a;
    const versioned = cfg;
    if (((_a = versioned.schemaVersion) !== null && _a !== void 0 ? _a : 1) >= 2)
        return cfg;
    const nextPages = cfg.pages.map((page) => ({
        ...page,
        components: page.components.map((c) => {
            if ((c.kind === "text" || c.kind === "clock" || c.kind === "content-box") &&
                typeof c.size === "number") {
                return { ...c, size: Math.max(8, Math.round(c.size * 0.48)) };
            }
            return c;
        }),
    }));
    return { ...cfg, pages: nextPages, schemaVersion: 2 };
}
function liftLegacyPages(r) {
    var _a, _b;
    if (Array.isArray(r.pages) && r.pages.length > 0) {
        return r;
    }
    const legacyTiles = r.tiles;
    const legacyGrid = r.grid;
    const main = {
        id: "main",
        name: "Main Menu",
        isMain: true,
        grid: (_a = legacyGrid) !== null && _a !== void 0 ? _a : DEFAULT_GRID,
        tiles: (_b = legacyTiles) !== null && _b !== void 0 ? _b : [],
        components: [],
    };
    const next = { ...r, pages: [main] };
    delete next.tiles;
    delete next.grid;
    return next;
}
function liftLegacyContentToComponents(cfg) {
    const legacyTheme = cfg.theme;
    const themeWelcome = legacyTheme.welcomeString;
    const themeLogo = legacyTheme.logoUrl;
    const nextPages = cfg.pages.map((page) => {
        var _a, _b, _c, _d, _e, _f, _g;
        if (Array.isArray(page.components))
            return page;
        const legacyPage = page;
        const comps = [];
        const welcomeText = (_a = legacyPage.welcomeString) !== null && _a !== void 0 ? _a : themeWelcome;
        if (welcomeText) {
            comps.push({
                id: nanoid(8),
                kind: "text",
                position: (_c = (_b = legacyPage.welcomePosition) !== null && _b !== void 0 ? _b : legacyTheme.welcomePosition) !== null && _c !== void 0 ? _c : DEFAULT_WELCOME_POSITION,
                text: welcomeText,
                font: legacyTheme.welcomeFont,
                color: legacyTheme.welcomeColor,
                size: legacyTheme.welcomeSize,
            });
        }
        const logoUrl = (_d = legacyPage.logoUrl) !== null && _d !== void 0 ? _d : themeLogo;
        if (logoUrl) {
            comps.push({
                id: nanoid(8),
                kind: "image",
                position: (_f = (_e = legacyPage.logoPosition) !== null && _e !== void 0 ? _e : legacyTheme.logoPosition) !== null && _f !== void 0 ? _f : DEFAULT_LOGO_POSITION,
                url: logoUrl,
                size: (_g = legacyTheme.logoSize) !== null && _g !== void 0 ? _g : DEFAULT_IMAGE_SIZE,
            });
        }
        const cleaned = { ...page, components: comps };
        delete cleaned.welcomeString;
        delete cleaned.welcomePosition;
        delete cleaned.logoUrl;
        delete cleaned.logoPosition;
        return cleaned;
    });
    const cleanedTheme = { ...cfg.theme };
    delete cleanedTheme.welcomeString;
    delete cleanedTheme.welcomeFont;
    delete cleanedTheme.welcomeColor;
    delete cleanedTheme.welcomeSize;
    delete cleanedTheme.welcomePosition;
    delete cleanedTheme.logoUrl;
    delete cleanedTheme.logoSize;
    delete cleanedTheme.logoPosition;
    return { ...cfg, theme: cleanedTheme, pages: nextPages };
}
function ensurePageDefaults(cfg) {
    let touched = false;
    const nextPages = cfg.pages.map((page) => {
        if (Array.isArray(page.components))
            return page;
        touched = true;
        return { ...page, components: [] };
    });
    return touched ? { ...cfg, pages: nextPages } : cfg;
}
function autofixTiles(cfg) {
    let touched = false;
    const nextPages = cfg.pages.map((page) => {
        let pageTouched = false;
        const nextTiles = page.tiles.map((tile) => {
            var _a;
            let next = tile;
            const legacy = next;
            if (legacy.action && !next.onClick) {
                next = { ...next, onClick: legacy.action };
                delete next.action;
                pageTouched = true;
                touched = true;
            }
            if (next.hideTitle === true && next.display === undefined) {
                next = { ...next, display: "icon-only" };
                pageTouched = true;
                touched = true;
            }
            if (((_a = next.onClick) === null || _a === void 0 ? void 0 : _a.kind) === "go-back" &&
                next.display === undefined &&
                next.hideTitle === undefined) {
                next = { ...next, display: "icon-only" };
                pageTouched = true;
                touched = true;
            }
            return next;
        });
        return pageTouched ? { ...page, tiles: nextTiles } : page;
    });
    return touched ? { ...cfg, pages: nextPages } : cfg;
}
